/**
 * A move's own sound, composed from its art's family (sfx/moves/families.ts) on its plan (sfx/moves/plan.ts):
 * gather → cut → blow per beat → tail, shaped by what the move does (a chain of blows, a ring round the caster, a
 * dash, a volley, a warp chain…), with the first-hour moves' own touches from the C0-SKILLFX beat list
 * (docs/research/C0_VFX.md §10.2-10.5, docs/research/C0_SOUND.md §4).
 */
import { artOfSkill, type SkillDef } from '@nine/shared';
import { BELL, type SfxKit } from '../kit';
import { getSfx } from '../registry';
import { chime, note, whoosh, woodblock } from '../voices';
import { accent, bloom, crystals, gather, statusLayer, swing, tail, TINK, type Family, type Vary } from './families';
import type { MovePlan } from './plan';

/** Lead of the cut before its blow lands (s). */
const CUT_LEAD = 0.16;

const varyOf = (p: MovePlan): Vary => ({ deg: p.deg, bright: p.bright, tail: p.tail, variant: p.variant });

/** Grace blows before `t`: small knocks of the weapon, ta-ta-TA. */
function grace(k: SfxKit, f: Family, p: MovePlan, t: number, v: Vary): void {
  for (let j = 1; j <= p.flam; j++) {
    const at = Math.max(0, t - 0.07 * j);
    if (f.weapon === 'palm' || f.weapon === 'fist' || f.weapon === 'heavy' || f.weapon === 'beast')
      k.thump({ f0: 170, f1: 80, at, dur: 0.06, vol: 0.12 });
    else k.click({ f: 1700 * v.bright, at, vol: 0.1, q: 4 });
  }
}

/** The whole move, from its `skill` event. */
export function composeMove(k: SfxKit, s: SkillDef, p: MovePlan, fam: Family): void {
  const v = varyOf(p);
  const f = fam;
  grace(k, f, p, p.strikes[0] ?? 0.15, v);
  const w = p.weight;
  const power = s.kind === 'power';
  const last = p.strikes.length ? p.strikes[p.strikes.length - 1]! : 0;
  const tailLen =
    (0.35 + w * 0.25 + (power ? 0.4 : 0)) * v.tail * (p.shape === 'scripted' ? 1 + Math.min(3, s.windup) * 0.25 : 1);
  // A monster's or a boss's move (no art): its effect's own gesture under the family's voice.
  if (!artOfSkill(s.id)) fxGesture(k, s);
  // A power move breathes in before its first blow.
  if (power && p.strikes.length && p.strikes[0]! > 0.2) gather(k, f, 0, p.strikes[0]! - 0.05, v);

  const blow = (t: number, i: number, weight: number = w) => {
    swing(k, f, t - CUT_LEAD, weight, { ...v, dir: i % 2 ? -1 : 1 });
    accent(k, f, t, weight, { ...v, deg: v.deg + (i % 3) });
  };

  switch (p.shape) {
    case 'single':
    case 'multi':
      p.strikes.forEach((t, i) => blow(t, i, i === p.strikes.length - 1 && p.strikes.length > 1 ? Math.min(2, w + 1) : w));
      break;
    case 'dashTarget':
      // Closing in: feet and a long rush of air before the blow.
      whoosh(k, { dur: 0.3, f0: 300, f1: 2200 * v.bright, f2: 900, vol: 0.16, q: 1.4, peak: 0.7 });
      for (let i = 0; i < 2; i++) k.thump({ f0: 140, f1: 80, at: i * 0.08, dur: 0.05, vol: 0.1 });
      p.strikes.forEach((t, i) => blow(t + 0.06, i));
      break;
    case 'dashForward':
      whoosh(k, { dur: 0.36, f0: 500, f1: 4200 * v.bright, f2: 1200, vol: 0.22, q: 2, peak: 0.55 });
      k.noise({ dur: 0.3, vol: 0.06, attack: 0.02, type: 'highpass', f: 5000 });
      p.strikes.forEach((t, i) => accent(k, f, t + 0.02, w, { ...v, deg: v.deg + i }));
      break;
    case 'aoe': {
      // A ring round the caster: the cut circles the listener, blows on either side.
      const pan = k.pan(0);
      k.lfo(pan.pan, 2.5 + v.variant * 0.6, 0.8, 0, 0.6);
      swing(k, f, 0, w, { ...v, dir: 1 });
      whoosh(k, { at: 0.04, dur: 0.45, f0: 400 * v.bright, f1: 2400 * v.bright, f2: 700, vol: 0.18, q: 1.6, out: pan });
      p.strikes.forEach((t, i) => {
        accent(k, f, t, w, v);
        accent(k, f, t + 0.07 + v.variant * 0.015, Math.max(0, w - 1), { ...v, deg: v.deg + 2 + i });
      });
      break;
    }
    case 'ground':
      // Sent out to a spot: a rising throw, then the spot bursting.
      whoosh(k, { dur: 0.25, f0: 500, f1: 3000 * v.bright, f2: 1400, vol: 0.15, q: 2 });
      p.strikes.forEach((t, i) => {
        accent(k, f, t + 0.04, Math.min(2, w + 1), { ...v, deg: v.deg + i });
        k.thump({ f0: 120, f1: 45, at: t + 0.04, dur: 0.25, vol: 0.22 });
      });
      break;
    case 'volley':
      gather(k, f, 0, p.strikes[0]! - 0.02, v);
      p.strikes.forEach((t, i) => {
        if (f.weapon === 'thrown' || f.motif === 'needle' || f.motif === 'hiss')
          k.tone({ f: (4600 - (i % 4) * 300) * v.bright, f1: 2400, at: t, dur: 0.12, vol: 0.03 });
        else {
          whoosh(k, { at: t, dur: 0.2, f0: 1200, f1: 5000 * v.bright, f2: 2600, vol: 0.07, q: 4 });
          k.metal({ f: note(f.root * 2, v.deg + i), at: t + 0.03, dur: 0.25, vol: 0.016 });
        }
        if (i % 3 === v.variant % 3) accent(k, f, t + 0.12, 0, { ...v, deg: v.deg + i });
      });
      break;
    case 'spin':
      gather(k, f, 0, 0.12, v);
      swing(k, f, 0.02, w, v);
      accent(k, f, 0.2, w, v);
      break;
    case 'blink':
      // The chain's opening: a breath in and the first vanish (each warp sounds on its own, `composeBeat`).
      gather(k, f, 0, 0.18, v);
      k.noise({ at: 0.12, dur: 0.12, vol: 0.2, attack: 0.1, f: 600, f1: 5200, q: 2.5, linear: true });
      break;
    case 'tele': {
      const d = p.strikes[0] ?? 1;
      gather(k, f, 0, d, v);
      accent(k, f, d, 2, v);
      break;
    }
    case 'heal':
      bloom(k, f, false, v, p.pattern);
      break;
    case 'buff':
      bloom(k, f, true, v, p.pattern);
      break;
    case 'scripted':
      gather(k, f, 0, 0.12, v);
      if (power) {
        // A boss's big move: borne up and brought down a beat later.
        whoosh(k, { at: 0.05, dur: 0.4, f0: 200, f1: 1800 * v.bright, f2: 300, vol: 0.2, q: 1.2, peak: 0.6 });
        swing(k, f, 0.3, 2, v);
        accent(k, f, 0.46, 2, v);
        k.thump({ f0: 70, f1: 26, at: 0.46, dur: 0.7, vol: 0.4 });
      } else {
        swing(k, f, 0.04, 2, v);
        accent(k, f, 0.2, 2, v);
      }
      break;
  }

  // A break shatters a guard: a crunch on its blow.
  if (s.kind === 'break' && p.strikes.length) {
    const t = p.strikes[0]!;
    k.noise({ color: 'crackle', at: t, dur: 0.18, vol: 0.4, type: 'bandpass', f: 1800 + v.variant * 200, q: 0.8 });
    k.thump({ f0: 230, f1: 90, at: t, dur: 0.1, vol: 0.2 });
  }
  if (p.lifesteal)
    // Drawn back into the hand: a reversed swell.
    k.noise({ at: last + 0.08, dur: 0.35, vol: 0.1, attack: 0.32, f: 2400, f1: 500, q: 3, linear: true });
  for (const st of p.status) statusLayer(k, st, last + 0.05);

  if (p.shape !== 'heal' && p.shape !== 'buff') tail(k, f, last + 0.1, tailLen, v);
  if (power) k.thump({ f0: 70, f1: 30, at: last, dur: 0.6, vol: 0.3 });

  EXTRA[s.id]?.(k, p, f, v);
}

/**
 * One beat of a long move: a warp of a chain (the `blink` event) or a whirlwind's turn (`spin`). `k.i` carries the
 * beat's place: under 1 the first beats, over 1 the escalating ones, 1.25 and over the finisher.
 */
export function composeBeat(k: SfxKit, _s: SkillDef, p: MovePlan, fam: Family): void {
  const v = varyOf(p);
  const f = fam;
  const i = k.i;
  const w = i >= 1.25 ? 2 : i > 1 ? 1 : 0;
  if (p.shape === 'blink') {
    // Gone (a reversed whoosh cut sharp), here (a crack where the fighter lands), the cut, the blow.
    k.noise({ dur: 0.1, vol: 0.18, attack: 0.09, f: 700, f1: 5600, q: 2.5, linear: true });
    k.noise({ at: 0.1, dur: 0.05, vol: 0.2, attack: 0.001, type: 'highpass', f: 4500 });
    swing(k, f, 0.06, w, { ...v, dir: Math.round(i * 10) % 2 ? 1 : -1 });
    accent(k, f, 0.2, w, { ...v, deg: v.deg + Math.round(i * 7) % 5 });
    if (w === 2) {
      // The finisher: a held breath (the hold is the rules' gap), then the great cut and a long tail.
      k.thump({ f0: 80, f1: 28, at: 0.2, dur: 0.6, vol: 0.4 });
      tail(k, f, 0.3, 0.9 * v.tail, v);
      chime(k, { f: note(f.root, v.deg + 4) * 2, at: 0.22, dur: 1.2, vol: 0.05 });
    }
  } else {
    whoosh(k, { dur: 0.22, f0: 400 * v.bright, f1: 2200 * v.bright, f2: 600, vol: 0.14, q: 1.6 });
    accent(k, f, 0.12, 0, { ...v, deg: v.deg + (Math.round(i * 10) % 3) });
  }
}

/** A family's blow on a target (the `hit` event's art layer), pitched per move by the caller. */
export function composeImpact(k: SfxKit, f: Family): void {
  accent(k, f, 0, 1, { deg: 0, bright: 1, tail: 1, variant: 1 });
}

/** A family's wind-up colour under the kind's warning (`castStart`), filling `len`. */
export function composeWindup(k: SfxKit, f: Family, len: number): void {
  gather(k, f, 0, len, { deg: 0, bright: 1, tail: 1, variant: 0 });
}

/** The gesture of a move's effect (`SkillDef.fx`): the effect's sound (sounds/skills.ts), or the dungeon bosses' own. */
function fxGesture(k: SfxKit, s: SkillDef): void {
  const d = getSfx(`skill.fx.${s.fx}`);
  if (d) return d.play(k, k.call);
  switch (s.fx) {
    case 'dgHarpoon':
      // A rope whipping out, the hook biting, the haul.
      whoosh(k, { dur: 0.3, f0: 600, f1: 3000, f2: 900, vol: 0.2, q: 2 });
      k.metal({ f: 1300, at: 0.28, dur: 0.3, vol: 0.06 });
      k.noise({ at: 0.32, dur: 0.4, vol: 0.12, attack: 0.05, f: 900, f1: 400, q: 3 });
      break;
    case 'dgSleep':
      // A blowpipe's puff and a sweet, drowsy cloud.
      k.noise({ dur: 0.1, vol: 0.25, attack: 0.005, type: 'highpass', f: 3000 });
      k.noise({ color: 'pink', at: 0.08, dur: 1, vol: 0.12, attack: 0.3, type: 'lowpass', f: 1500, f1: 600 });
      k.tone({ f: 660, f1: 440, at: 0.2, dur: 0.9, vol: 0.02, vib: [3, 0.03] });
      break;
    case 'dgChain':
      for (let i = 0; i < 10; i++) k.metal({ f: k.range(1700, 3000), at: i * 0.05, dur: 0.12, vol: 0.02 , partials: TINK });
      k.thump({ f0: 150, f1: 60, at: 0.5, dur: 0.2, vol: 0.3 });
      break;
    case 'dgWhirlpool': {
      const pan = k.pan(0);
      k.lfo(pan.pan, 1.2, 0.9, 0, 2);
      k.noise({ color: 'pink', dur: 2, vol: 0.25, attack: 0.4, type: 'lowpass', f: 900, f1: 250, trem: [6, 0.5], out: pan });
      k.tone({ f: 70, f1: 45, dur: 2, vol: 0.08, attack: 0.5 });
      break;
    }
    case 'dgIron':
      k.metal({ f: 180, dur: 1.4, vol: 0.1, partials: BELL, drift: 0.004 });
      k.thump({ f0: 140, f1: 50, dur: 0.3, vol: 0.35 });
      k.noise({ color: 'brown', dur: 0.4, vol: 0.2, type: 'lowpass', f: 500 });
      break;
  }
}

// ───────── the first-hour moves' own touches (C0-SKILLFX beat list) ─────────

type Extra = (k: SfxKit, p: MovePlan, f: Family, v: Vary) => void;

const at0 = (p: MovePlan) => p.strikes[0] ?? 0.19;

const EXTRA: Readonly<Record<string, Extra>> = {
  // กระบี่ไผ่ห่มหมอก
  'bs-gust': (k, p) => k.noise({ at: at0(p) - 0.05, dur: 0.25, vol: 0.1, attack: 0.04, f: 1500, f1: 5200, q: 3 }),
  'bs-cloud': (k, p) => {
    // The "now" glint at 75 % of the wind-up already sounded (castStart); the pierce goes through a puff of cloud.
    k.tone({ f: 2600, f1: 3900, at: at0(p) - 0.04, dur: 0.1, vol: 0.03 });
    k.noise({ color: 'pink', at: at0(p), dur: 0.3, vol: 0.14, attack: 0.03, type: 'lowpass', f: 700, f1: 300 });
  },
  'bs-sweep': (k) => {
    for (let i = 0; i < 6; i++) k.noise({ color: 'crackle', at: 0.05 + i * 0.05, dur: 0.08, vol: 0.12, type: 'highpass', f: 3400 });
  },
  'bs-bamboo': (k) => {
    // Bamboo leaves drawn in and spiralling up: knocks of bamboo climbing the scale.
    for (let i = 0; i < 4; i++) woodblock(k, { f: note(587.3, i), at: 0.15 + i * 0.12, vol: 0.05 });
  },
  'bs-glide': (k) => {
    for (let i = 0; i < 3; i++) k.thump({ f0: 150, f1: 90, at: 0.05 + i * 0.09, dur: 0.05, vol: 0.08 });
  },
  'bs-mist': (k) => {
    const pan = k.pan(0);
    k.lfo(pan.pan, 1.5, 0.7, 0, 1);
    k.noise({ color: 'pink', dur: 1, vol: 0.1, attack: 0.3, f: 900, f1: 1500, q: 2, out: pan });
  },
  'bs-reed': (k, p) => {
    // Reed fluff on each of the three cuts.
    p.strikes.forEach((t, i) => k.noise({ at: t + 0.02, dur: 0.1, vol: 0.07, attack: 0.02, f: 6000 - i * 600, q: 5 }));
  },
  'bs-tempest': (k) => {
    k.noise({ color: 'pink', dur: 1.3, vol: 0.18, attack: 0.25, f: 500, f1: 1600, f2: 600, sweep: 0.7, q: 2, trem: [5, 0.5] });
    for (let i = 0; i < 8; i++) k.noise({ color: 'crackle', at: 0.3 + i * 0.09, dur: 0.1, vol: 0.12, type: 'highpass', f: 3000 });
  },
  // ฝ่ามือเมฆาคล้อย
  'cp-wave': (k, p) => k.noise({ color: 'pink', at: at0(p) + 0.03, dur: 0.4, vol: 0.12, attack: 0.05, f: 500, f1: 1200, q: 1.5 }),
  'cp-seize': (k, p) => k.thump({ f0: 200, f1: 70, at: at0(p) + 0.08, dur: 0.15, vol: 0.25 }),
  'cp-storm': (k) => {
    const pan = k.pan(0);
    k.lfo(pan.pan, 3, 0.9, 0, 0.8);
    k.noise({ color: 'pink', dur: 0.8, vol: 0.16, attack: 0.15, type: 'lowpass', f: 900, f1: 400, out: pan });
  },
  'cp-breath': (k) => {
    for (let i = 0; i < 3; i++) k.noise({ color: 'pink', at: 0.2 + i * 0.3, dur: 0.35, vol: 0.12, attack: 0.15, f: 1400, f1: 2400, q: 1.5 });
  },
  'cp-drift': (k) => whoosh(k, { at: 0.1, dur: 0.3, f0: 1500, f1: 5000, f2: 2500, vol: 0.08, q: 3 }),
  'cp-roar': (k, p) => {
    // A cloud shockwave travelling to the foe: its pitch falls as it goes.
    k.noise({ color: 'brown', at: at0(p), dur: 0.5, vol: 0.3, attack: 0.02, type: 'lowpass', f: 900, f1: 200 });
    k.tone({ type: 'triangle', f: 220, f1: 110, at: at0(p), dur: 0.4, vol: 0.05 });
  },
  'cp-ring': (k, p) => {
    for (let i = 0; i < 6; i++) k.noise({ color: 'pink', at: Math.max(0, at0(p) - 0.2 + i * 0.04), dur: 0.15, vol: 0.08, attack: 0.03, type: 'lowpass', f: 700 });
    crystals(k, 4, at0(p) + 0.05, 0.2, 0.015);
  },
  'cp-heaven': (k) => {
    for (let i = 0; i < 5; i++) k.noise({ color: 'pink', at: 0.5 + i * 0.12, dur: 0.3, vol: 0.12, attack: 0.04, type: 'lowpass', f: 600 - i * 50 });
  },
  // ดาบชาดคลั่งไฟ
  'bs-slash': (k, p) => k.noise({ at: at0(p), dur: 0.03, vol: 0.2, attack: 0.001, type: 'highpass', f: 4200 }),
  'bs-rend': (k, p) => p.strikes.forEach((t) => k.noise({ color: 'brown', at: t + 0.05, dur: 0.15, vol: 0.2, f: 700, q: 1.2, trem: [34, 0.8] })),
  'bs-whirl': (k) => {
    for (let i = 0; i < 5; i++) k.tone({ f: 300 - i * 20, f1: 700, at: 0.3 + i * 0.05, dur: 0.05, vol: 0.03 });
  },
  'bs-charge': (k) => {
    for (let i = 0; i < 4; i++) k.thump({ f0: 130, f1: 70, at: i * 0.07, dur: 0.06, vol: 0.12 });
  },
  'bs-break': (k, p) => {
    // Bone cracking under the blow.
    for (let i = 0; i < 3; i++) k.click({ f: 1100 + i * 300, at: at0(p) + 0.01 + i * 0.02, vol: 0.2, q: 6 });
  },
  'bs-frenzy': (k) => {
    k.noise({ color: 'crackle', dur: 0.9, vol: 0.3, attack: 0.2, type: 'highpass', f: 1300 });
    k.tone({ type: 'sawtooth', f: 73, f1: 98, dur: 0.9, vol: 0.05, attack: 0.3, out: k.filter('lowpass', 500) });
  },
  'bs-drink': (k, p) => k.noise({ color: 'brown', at: at0(p) + 0.15, dur: 0.4, vol: 0.18, attack: 0.35, f: 300, f1: 900, q: 2, linear: true }),
  'bs-massacre': (k) => k.tone({ type: 'sawtooth', f: 55, dur: 1.4, vol: 0.08, attack: 0.2, out: k.filter('lowpass', 380) }),
  // กระบี่แท่งหมึกสน
  'jz-stroke': (k, p) => k.noise({ color: 'pink', at: at0(p) - 0.1, dur: 0.22, vol: 0.12, attack: 0.08, f: 900, f1: 2200, q: 1.4 }),
  'jz-poem': (k, p) => p.strikes.forEach((t, i) => k.pluck({ f: note(392, i * 2), at: t, dur: 0.5, vol: 0.07, bright: 0.4 })),
  'jz-ink': (k) => {
    for (let i = 0; i < 6; i++) k.tone({ f: 280 + i * 15, f1: 900, at: 0.25 + i * 0.04, dur: 0.06, vol: 0.03 });
  },
  'jz-seal': (k, p) => {
    // A pressure point pressed: one bright pluck and ripples spreading.
    k.pluck({ f: 784, at: at0(p), dur: 0.7, vol: 0.12, bright: 0.85 });
    for (let i = 0; i < 3; i++) k.tone({ f: 1200 - i * 200, at: at0(p) + 0.05 + i * 0.08, dur: 0.15, vol: 0.02 });
  },
  'jz-ward': (k) => {
    // An ink fan opening rib by rib.
    for (let i = 0; i < 7; i++) k.click({ f: 1800 + i * 120, at: 0.05 + i * 0.045, vol: 0.08, q: 5 });
  },
  'jz-focus': (k) => {
    for (let i = 0; i < 3; i++) k.tone({ f: 1567 + i * 200, at: 0.2 + i * 0.15, dur: 0.4, vol: 0.015, attack: 0.08 });
  },
  'jz-break': (k, p) => {
    k.noise({ color: 'crackle', at: at0(p), dur: 0.3, vol: 0.35, type: 'highpass', f: 2000 });
    k.pluck({ f: 196, at: at0(p), dur: 0.8, vol: 0.1, bright: 0.25 });
  },
  'jz-scroll': (k) => {
    // The ink wash rolling out as a wave.
    k.noise({ color: 'pink', at: 0.3, dur: 1, vol: 0.18, attack: 0.3, f: 400, f1: 1600, f2: 500, sweep: 0.5, q: 1.2 });
    for (let i = 0; i < 5; i++) k.pluck({ f: note(392, -i), at: 0.4 + i * 0.1, dur: 0.6, vol: 0.05, bright: 0.35 });
  },
  // The 8-beat warp ultimate (เหมันต์สะกดธาร): the opening carries a held, rising frost under the whole chain.
  'ss-hatred': (k) => k.noise({ dur: 3, vol: 0.05, attack: 2.6, f: 1200, f1: 6000, q: 5, linear: true }),
};
